import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ImagePlus, X } from "lucide-react";

import AuthService from "@/services/authService";

const BrandLogo = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    AuthService.getLogo()
      .then((res) => {
        if (res) setLogo(res);
      })
      .finally(() => setLoading(false));
  }, []);

  const onClearLogo = () => {
    AuthService.deleteLogo().then(() => {
      setLogo(null);
      setImgLoaded(false);
    });
  };

  const onUploadLogo = (e) => {
    const file = e.target.files[0];
    AuthService.uploadLogo(file).then(() => {
      setImgLoaded(false);
      AuthService.getLogo().then((res) => {
        setLogo(res);
      });
    });
  };

  // Logo flash'ını (EPS-4937) önlemek için: API dönene kadar VE logo görseli
  // tam yüklenene (<img onLoad>) kadar, logonun GERÇEK boyutunda (~180×48)
  // nötr skeleton göster. Böylece "logo ekleyin" placeholder'ı yükleme sırasında
  // hiç görünmez ve skeleton→logo tek geçişte, boyut değişmeden olur.
  // "logo ekleyin" placeholder'ı YALNIZCA yükleme bittikten sonra gerçekten
  // logosu olmayan firmada gösterilir (aşağıdaki son iki return).
  const showSkeleton = loading || (logo && !imgLoaded);

  if (loading || logo) {
    return (
      <motion.div
        layout
        className="relative border-b border-[#e5e5e7]/60 pb-6"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {showSkeleton && (
          <div
            className={`animate-pulse rounded-lg bg-[#f5f5f7] ${
              collapsed ? "mx-auto h-12 w-12" : "mx-auto h-12 w-[180px] max-w-full"
            }`}
          />
        )}

        {logo && (
          <div
            className={`flex items-center justify-center ${
              showSkeleton ? "hidden" : ""
            }`}
          >
            <motion.img
              layout
              src={logo}
              alt="logo"
              onLoad={() => setImgLoaded(true)}
              className={`object-contain transition-all duration-300 ${
                collapsed ? "max-h-10 max-w-[48px]" : "max-h-12"
              }`}
            />
          </div>
        )}

        <AnimatePresence>
          {logo && imgLoaded && isHovered && !collapsed && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClearLogo}
              className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.04] transition-colors hover:bg-red-500 hover:text-white"
            >
              <X size={12} strokeWidth={2.5} />
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  if (collapsed) {
    return (
      <div className="border-b border-[#e5e5e7]/60 pb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => document.getElementById("file").click()}
          className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] transition-all hover:ring-black/[0.08]"
        >
          <ImagePlus size={18} className="text-[#86868b]" />
        </motion.button>
        <input
          id="file"
          type="file"
          accept=".jpg, .jpeg, .png"
          className="hidden"
          onChange={(e) => onUploadLogo(e)}
        />
      </div>
    );
  }

  return (
    <div className="border-b border-[#e5e5e7]/60 pb-6">
      <motion.div
        whileHover={{ scale: 1.01 }}
        role="button"
        className="group flex cursor-pointer items-center justify-center rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/[0.04] transition-all hover:ring-black/[0.08]"
        onClick={() => document.getElementById("file").click()}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f5f5f7] transition-colors group-hover:bg-[#e5e5e7]">
            <ImagePlus size={18} className="text-[#86868b]" />
          </div>
          <span className="text-center text-[13px] font-medium tracking-[-0.01em] text-[#86868b]">
            {t("common.toAddLogo")}
          </span>
          <span className="text-[13px] font-medium tracking-[-0.01em] text-[#0049E6]">
            {t("common.clickHere")}
          </span>
        </div>
        <input
          id="file"
          type="file"
          accept=".jpg, .jpeg, .png"
          className="hidden"
          onChange={(e) => onUploadLogo(e)}
        />
      </motion.div>
    </div>
  );
};

export default BrandLogo;
